import { Box, Button, List, Popover, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, useState } from 'react';
import LangEnum from '../../../../../../../../../../lang/LangEnum';
import { useApplicationContext } from '../../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../../../../../translation/TranslationContext';
import { formatChatTime } from '../../../../../../../../utils/chat.formatters';
import ChatAvatar from '../../../../../../../ChatAvatar/ChatAvatar';
import { useChatMessageItemStyles } from './chatMessageItem.styles';
import { ChatMessageItemProps } from './chatMessageItem.types';
import ChatMessageByMessageType from './components/ChatMessageByMessageType/ChatMessageByMessageType';
import ChatMessageItemHeader from './components/ChatMessageItemHeader/ChatMessageItemHeader';
import useChatMessageItem from './hooks/useChatMessageItem';

import ListItemButton from '@mui/material/ListItemButton';

const ChatMessageItem = ({
  message,
  showUserAvatar,
  showMessageInfo,
  onClickRetry,
  reserveSpaceOfUserAvatar,
}: ChatMessageItemProps) => {
  const theme = useTheme();
  const { translate, locale } = useTranslation();
  const { team } = useApplicationContext();

  const { classes } = useChatMessageItemStyles();

  const [errorListActionsAnchorEl, setErrorListActionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const { handleClickListItem, handleClickRemove, handleClickRetry } = useChatMessageItem({
    onClickRetry,
    closeErrorListActions: () => setErrorListActionsAnchorEl(null),
  });

  const handleOpenAnchorEl = (event: React.MouseEvent<HTMLButtonElement>) => {
    setErrorListActionsAnchorEl(event.currentTarget);
  };

  const [showTooltip, setShowTooltip] = useState(false);

  const handleShowTooltip = () => (shouldRenderMessageTimestamp ? setShowTooltip(true) : null);
  const handleHideTooltip = () => setShowTooltip(false);

  const isMessageFromUser = message.user.id === team?.userId;
  const shouldRenderMessageTimestamp = !showMessageInfo;

  return (
    <>
      <Box
        className={classes.wrapper}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingX: 2,
          paddingY: 1,
          gap: '8px',
        }}
      >
        {showMessageInfo && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: isMessageFromUser ? 'row-reverse' : 'row',
            }}
          >
            <ChatMessageItemHeader message={message} showMessageInfo={showMessageInfo} />
          </Box>
        )}

        <Tooltip
          open={showTooltip}
          onOpen={handleShowTooltip}
          onClose={handleHideTooltip}
          title={formatChatTime(message.createdAt, locale)}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              flexDirection: 'row',
            }}
          >
            {reserveSpaceOfUserAvatar && (
              <Box className={classes.avatar}>
                {showUserAvatar && (
                  <ChatAvatar
                    title={message.user.name}
                    src={message.user.profileUrl}
                    backgroundColor={
                      message.user.profileUrl ? 'transparent' : theme.palette.grey[500]
                    }
                  />
                )}
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                mt: '4px',
                display: 'flex',
                gap: '4px',
                justifyContent: isMessageFromUser ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '88%',
                }}
              >
                <ChatMessageByMessageType message={message} />
              </Box>
            </Box>
          </Box>
        </Tooltip>

        {message.error && (
          <Box
            className={classes.handleErrorMessageWrapper}
            sx={{
              justifyContent: isMessageFromUser ? 'flex-end' : 'flex-start',
            }}
          >
            <Button variant="text" onClick={handleOpenAnchorEl}>
              <Typography variant="body2" color="error">
                {translate(LangEnum.CHAT_ERROR_SENDING_MESSAGE)}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
      <Popover
        keepMounted={false}
        open={!!errorListActionsAnchorEl}
        anchorEl={errorListActionsAnchorEl}
        onClose={() => setErrorListActionsAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <List>
          <ListItemButton onClick={() => handleClickListItem(() => handleClickRetry(message))}>
            <Typography variant="body2">{translate(LangEnum.GLOBAL_RETRY)}</Typography>
          </ListItemButton>
          <ListItemButton onClick={() => handleClickListItem(() => handleClickRemove(message))}>
            <Typography variant="body2">{translate(LangEnum.GLOBAL_REMOVE)}</Typography>
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};

export default memo(ChatMessageItem);
